import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import es from "./locales/es.json";
import en from "./locales/en.json";
import pt from "./locales/pt.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import it from "./locales/it.json";
import zh from "./locales/zh.json";
import ja from "./locales/ja.json";
import ru from "./locales/ru.json";
import {
  SUPPORTED_LANGUAGES,
  detectLanguageFromCountry,
  detectLanguageFromNavigator,
  detectLanguageFromTimeZone,
  type SupportedLanguage,
} from "./countryLanguageMap";

const STORAGE_KEY = "blackred-studio.language.v1";
const CLOUD_GEO_URL = "https://api.blackrednow.com/api/geo";

const resources = {
  es: { common: es },
  en: { common: en },
  pt: { common: pt },
  fr: { common: fr },
  de: { common: de },
  it: { common: it },
  zh: { common: zh },
  ja: { common: ja },
  ru: { common: ru },
};

function getStoredLanguage(): SupportedLanguage | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value && (SUPPORTED_LANGUAGES as readonly string[]).includes(value) ? (value as SupportedLanguage) : null;
  } catch {
    return null;
  }
}

function getTimeZoneLanguage(): SupportedLanguage | null {
  try {
    return detectLanguageFromTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

export function setLanguage(lang: SupportedLanguage) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // localStorage puede no estar disponible (modo privado); no es crítico.
  }
  void i18n.changeLanguage(lang);
}

void i18n.use(initReactI18next).init({
  resources,
  lng: getStoredLanguage() ?? getTimeZoneLanguage() ?? detectLanguageFromNavigator() ?? "en",
  fallbackLng: "en",
  supportedLngs: SUPPORTED_LANGUAGES,
  load: "languageOnly",
  defaultNS: "common",
  returnEmptyString: false,
  interpolation: { escapeValue: false },
});

function updateDocumentLanguage(lang: string) {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang.slice(0, 2).toLowerCase();
  document.documentElement.dir = "ltr";
}

i18n.on("languageChanged", updateDocumentLanguage);
updateDocumentLanguage(i18n.language);

/** Respaldo por país vía la Cloudflare Pages Function `/api/geo` (lee la
 *  cabecera `cf-ipcountry`, presente en toda request que pasa por Cloudflare).
 *  En web prueba primero el origen actual. Android y Electron consultan además
 *  el dominio público de la API, porque sus orígenes locales no pasan por Pages. */
export async function applyGeoLanguage() {
  if (getStoredLanguage()) return;
  const urls = typeof location !== "undefined" && /^https?:$/.test(location.protocol)
    ? ["/api/geo", CLOUD_GEO_URL]
    : [CLOUD_GEO_URL];
  for (const url of urls) {
    try {
      const response = await fetch(url, { headers: { accept: "application/json" } });
      if (!response.ok) continue;
      const data = (await response.json()) as { country?: string };
      const lang = detectLanguageFromCountry(data.country);
      if (lang && i18n.language !== lang) await i18n.changeLanguage(lang);
      if (lang) return;
    } catch {
      // Prueba el siguiente origen; zona horaria y navegador siguen como respaldo.
    }
  }
}

export default i18n;
